import { AvailableGdsServices, Step, StepType } from './types';
import { StepsEnum } from '../types';
import { seatMapHasAnySeats } from '../SeatMap/utils';
import {
	BaggageType,
	CheckoutOrderFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderStatus,
	PaymentStatusEnum
} from '@websky/graphql';
import { removeServicesStepsByTypes } from './utils';
import { GlobalConfig } from '../configSchema/GlobalConfigSchema';
import { applyServicesStepsOrder } from './servicesStepsOrder';

const SeatsStep: Step = {
	url: 'seats',
	type: StepsEnum.Seats
};

const BaggageStep: Step = {
	url: 'baggage',
	type: StepsEnum.Baggage
};

const MealStep: Step = {
	url: 'meal',
	type: StepsEnum.Meal
};

const PassengersStep: Step = {
	url: 'passengers',
	type: StepsEnum.Passengers
};

const InsuranceStep: Step = {
	url: 'insurance',
	type: StepsEnum.Insurance
};

const OrderStep: Step = {
	url: 'order',
	type: StepsEnum.Order
};

const PaymentStep: Step = {
	url: 'payment',
	type: StepsEnum.Payment
};

export const DEFAULT_STEPS_CONFIG: Step[] = [
	SeatsStep,
	BaggageStep,
	MealStep,
	PassengersStep,
	InsuranceStep,
	OrderStep,
	PaymentStep
];

export const SHOW_SERVICES_AFTER_BOOKING_STEPS: Step[] = [
	PassengersStep,
	SeatsStep,
	BaggageStep,
	MealStep,
	InsuranceStep,
	OrderStep,
	PaymentStep
];

export const PRICE_FREEZE_STEPS_CONFIG: Step[] = [PassengersStep, OrderStep, PaymentStep];

interface StepsAvailabilityParams {
	order: CheckoutOrderFragment;
	availableServices: AvailableGdsServices;
	showServicesBeforeBooking: boolean;
	hasAvailableSeats: boolean;
	configStepAvailable: GlobalConfig['stepsAvailable'];
	configServicesAvailable?: GlobalConfig['servicesAvailable'];
	isPaymentRequired: boolean;
	servicesStepsOrder?: StepsEnum[];
}

const isSeatsAvailable = (order: CheckoutOrderFragment, availableServices: AvailableGdsServices) => {
	return (
		(seatMapHasAnySeats(order.flight.seatMap) &&
			availableServices.hasOwnProperty(OrderAdditionalServiceGdsServiceServiceType.Seat)) ||
		order.flight.seatMap?.segments?.some(segment => segment.isSeatsAvailableInCheckinOnly)
	);
};

const isInsuranceAvailable = (order: CheckoutOrderFragment) => {
	if (
		!order.additionalServices.insurances?.insurancePrograms?.length ||
		!order.additionalServices?.insurances?.separateStep
	) {
		return false;
	}

	return order.status !== OrderStatus.Confirmed;
};

const isMealsAvailable = (order: CheckoutOrderFragment, availableServices: AvailableGdsServices) => {
	const mealServices =
		order.additionalServices?.gdsServices?.services?.filter(
			service => service?.type === OrderAdditionalServiceGdsServiceServiceType.Meal
		) ?? [];

	const segmentsIds = order.flight?.segments?.map(segment => segment?.segment?.id);

	const hasAvailableMealServices = mealServices.some(meal =>
		meal?.allowedSegments?.some(groups => groups?.some(segment => segmentsIds.includes(segment)))
	);

	return (
		availableServices.hasOwnProperty(OrderAdditionalServiceGdsServiceServiceType.Meal) && hasAvailableMealServices
	);
};

const isBaggageAvailable = (
	availableServices: AvailableGdsServices,
	configServicesAvailable: GlobalConfig['servicesAvailable']
) => {
	const animalAsService = !!configServicesAvailable?.animal;

	return Object.values(BaggageType)
		.filter(type =>
			animalAsService
				? ![BaggageType.PetInCabin, BaggageType.PetInHold, BaggageType.PetInHandCarrier].includes(type)
				: ![BaggageType.PetInHandCarrier].includes(type)
		)
		.some(type => availableServices.hasOwnProperty(type));
};

const isNeedToSwapPassengersAndOrder = (order: CheckoutOrderFragment) =>
	order.isPriceFreezeProductSelected && order.paymentStatus === PaymentStatusEnum.Part;

const swapPassengersAndOrder = (steps: Step[]) => {
	const passengersIndex = steps.findIndex(step => step.type === StepsEnum.Passengers),
		orderIndex = steps.findIndex(step => step.type === StepsEnum.Order);

	if (passengersIndex < orderIndex && passengersIndex >= 0 && orderIndex >= 0) {
		const tmpArray = [...steps];

		tmpArray[passengersIndex] = steps[orderIndex];
		tmpArray[orderIndex] = steps[passengersIndex];

		return tmpArray;
	}

	return steps;
};

const isStepAvailable = (step: Step, params: StepsAvailabilityParams) => {
	const { order, configStepAvailable, hasAvailableSeats, availableServices, isPaymentRequired } = params;

	const isBooked = order.status === OrderStatus.Booked;
	const isBookedOrConfirmed = isBooked || order.status === OrderStatus.Confirmed;

	if (
		(order.status === OrderStatus.Cancelled && step.type !== StepType.Flights && step.type !== StepType.Order) ||
		(step?.conditions?.bookedOnly && !isBooked) ||
		(!isBookedOrConfirmed && configStepAvailable?.[step.type] === false)
	) {
		return false;
	}

	switch (step.type) {
		case StepsEnum.Seats:
			return hasAvailableSeats && isSeatsAvailable(order, availableServices);
		case StepsEnum.Baggage:
			return isBaggageAvailable(availableServices, params.configServicesAvailable);
		case StepsEnum.Insurance:
			return isInsuranceAvailable(order);
		case StepsEnum.Meal:
			return isMealsAvailable(order, availableServices);
		case StepsEnum.Payment:
			return isPaymentRequired;
	}

	return true;
};

const createStepCheckingFunc = (params: StepsAvailabilityParams) => {
	return (step: Step) => isStepAvailable(step, params);
};

export const getSteps = (params: StepsAvailabilityParams) => {
	const isStepAvailable = createStepCheckingFunc(params);

	let filteredSteps = getStepsConfig(params);
	filteredSteps = applyServicesStepsOrder(filteredSteps, params.servicesStepsOrder);
	const passengersIndex = filteredSteps.findIndex(step => step.type === StepsEnum.Passengers);

	filteredSteps = filteredSteps.map((step, stepIndex) => ({
		...step,
		hidden: !isStepAvailable(step),
		disabled:
			params.order.status === OrderStatus.Booked &&
			!params.order.isPriceFreezeProductSelected &&
			(stepIndex < passengersIndex || (step.type === StepsEnum.Passengers && params.order.isTravellersDataFilled))
	}));

	if (isNeedToSwapPassengersAndOrder(params.order)) {
		return swapPassengersAndOrder(filteredSteps);
	}

	return filteredSteps;
};

const getStepsConfig = (params: StepsAvailabilityParams): Step[] => {
	if (
		params.order.isPriceFreezeProductSelected &&
		(params.order.status === OrderStatus.New || !params.order.isTravellersDataFilled)
	) {
		return PRICE_FREEZE_STEPS_CONFIG;
	}

	if (!params.showServicesBeforeBooking) {
		return SHOW_SERVICES_AFTER_BOOKING_STEPS;
	}

	return DEFAULT_STEPS_CONFIG;
};

export const removeStepsWhenRedirectFromMetasearch = (steps: Step[]) => {
	return removeServicesStepsByTypes(steps, [StepType.Seats, StepType.Meal, StepType.Baggage]);
};

export const removeStepsWhenOrderHasPassengersData = (steps: Step[]) => {
	return removeServicesStepsByTypes(steps, [StepType.Passengers]);
};
